// ============================================================================
// WHS service — thin wrappers over the shared axios instance. Every method
// returns the ApiResponse<T> envelope; callers narrow on `success`.
// ============================================================================
import apiService from './api';
import type { ApiResponse } from '../types';
import type {
  WhsSettings, ResponsiblePerson, RiskMatrix,
  ReportListItem, WhsReport, TrackedReport, CreateReportPayload, CreateReportResult,
  ReportUpdate, UnmaskResult, ReportStatus, ReportType,
  Investigation,
  WhsAction, ActionStatus, ActionSource,
  RiskAssessmentListItem, RiskAssessment, RiskItem, RiskAssessmentStatus, RiskAssessmentType,
  ChecklistTemplate, ChecklistKind,
  InspectionListItem, Inspection, InspectionStatus, InspectionSchedule,
  AuditListItem, Audit, AuditFinding, AuditStatus,
  WhsDocument, DocumentType, DocumentStatus,
  TrainingRecord,
  EmergencyPlan,
  Objective,
  ManagementReview,
  DashboardStats, FarmOption,
} from '../types/whs';

const qs = (params: Record<string, unknown>): string => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
};

class WhsService {
  // ─── Dashboard ────────────────────────────────────────────────────────────
  getDashboard(days = 90): Promise<ApiResponse<DashboardStats>> {
    return apiService.get(`/whs/dashboard${qs({ days })}`);
  }

  // ─── Settings & governance ─────────────────────────────────────────────────
  getSettings(): Promise<ApiResponse<WhsSettings>> {
    return apiService.get('/whs/settings');
  }
  updateSettings(data: Partial<WhsSettings>): Promise<ApiResponse<WhsSettings>> {
    return apiService.put('/whs/settings', data);
  }
  getRiskMatrix(): Promise<ApiResponse<RiskMatrix>> {
    return apiService.get('/whs/risk-matrix');
  }
  listResponsiblePersons(activeOnly = false): Promise<ApiResponse<ResponsiblePerson[]>> {
    return apiService.get(`/whs/responsible-persons${qs({ active: activeOnly ? 'true' : undefined })}`);
  }
  createResponsiblePerson(data: Partial<ResponsiblePerson>): Promise<ApiResponse<ResponsiblePerson>> {
    return apiService.post('/whs/responsible-persons', data);
  }
  updateResponsiblePerson(id: number, data: Partial<ResponsiblePerson>): Promise<ApiResponse<ResponsiblePerson>> {
    return apiService.put(`/whs/responsible-persons/${id}`, data);
  }
  deleteResponsiblePerson(id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiService.delete(`/whs/responsible-persons/${id}`);
  }

  // ─── Worker reporting (available to any authenticated user) ─────────────────
  submitReport(data: CreateReportPayload): Promise<ApiResponse<CreateReportResult>> {
    return apiService.post('/whs/reports', data);
  }
  trackReport(code: string): Promise<ApiResponse<TrackedReport>> {
    return apiService.get(`/whs/reports/track/${encodeURIComponent(code)}`);
  }
  getReportOptions(): Promise<ApiResponse<{ farms: FarmOption[] }>> {
    return apiService.get('/whs/report-options');
  }

  // ─── Reports management ─────────────────────────────────────────────────────
  listReports(filters: {
    status?: ReportStatus; report_type?: ReportType; assigned_to?: number;
    is_notifiable?: boolean; search?: string;
  } = {}): Promise<ApiResponse<ReportListItem[]>> {
    return apiService.get(`/whs/reports${qs(filters)}`);
  }
  getReport(id: number): Promise<ApiResponse<WhsReport>> {
    return apiService.get(`/whs/reports/${id}`);
  }
  updateReport(id: number, data: Record<string, unknown>): Promise<ApiResponse<WhsReport>> {
    return apiService.put(`/whs/reports/${id}`, data);
  }
  addReportUpdate(id: number, data: Partial<ReportUpdate>): Promise<ApiResponse<ReportUpdate>> {
    return apiService.post(`/whs/reports/${id}/updates`, data);
  }
  unmaskReporter(id: number): Promise<ApiResponse<UnmaskResult>> {
    return apiService.post(`/whs/reports/${id}/unmask`, {});
  }

  // ─── Investigations ─────────────────────────────────────────────────────────
  createInvestigation(data: Partial<Investigation>): Promise<ApiResponse<Investigation>> {
    return apiService.post('/whs/investigations', data);
  }
  getInvestigation(id: number): Promise<ApiResponse<Investigation>> {
    return apiService.get(`/whs/investigations/${id}`);
  }
  updateInvestigation(id: number, data: Partial<Investigation>): Promise<ApiResponse<Investigation>> {
    return apiService.put(`/whs/investigations/${id}`, data);
  }

  // ─── Actions / CAPA ─────────────────────────────────────────────────────────
  listActions(filters: {
    status?: ActionStatus; assigned_to?: number; source_type?: ActionSource;
    source_id?: number; overdue?: boolean;
  } = {}): Promise<ApiResponse<WhsAction[]>> {
    return apiService.get(`/whs/actions${qs(filters)}`);
  }
  createAction(data: Partial<WhsAction>): Promise<ApiResponse<WhsAction>> {
    return apiService.post('/whs/actions', data);
  }
  updateAction(id: number, data: Partial<WhsAction>): Promise<ApiResponse<WhsAction>> {
    return apiService.put(`/whs/actions/${id}`, data);
  }
  deleteAction(id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiService.delete(`/whs/actions/${id}`);
  }

  // ─── Risk assessments + items ───────────────────────────────────────────────
  listRiskAssessments(filters: { status?: RiskAssessmentStatus; assessment_type?: RiskAssessmentType } = {}): Promise<ApiResponse<RiskAssessmentListItem[]>> {
    return apiService.get(`/whs/risk-assessments${qs(filters)}`);
  }
  getRiskAssessment(id: number): Promise<ApiResponse<RiskAssessment>> {
    return apiService.get(`/whs/risk-assessments/${id}`);
  }
  createRiskAssessment(data: Partial<RiskAssessment>): Promise<ApiResponse<RiskAssessment>> {
    return apiService.post('/whs/risk-assessments', data);
  }
  updateRiskAssessment(id: number, data: Record<string, unknown>): Promise<ApiResponse<RiskAssessment>> {
    return apiService.put(`/whs/risk-assessments/${id}`, data);
  }
  deleteRiskAssessment(id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiService.delete(`/whs/risk-assessments/${id}`);
  }
  createRiskItem(assessmentId: number, data: Partial<RiskItem>): Promise<ApiResponse<RiskItem>> {
    return apiService.post(`/whs/risk-assessments/${assessmentId}/items`, data);
  }
  updateRiskItem(id: number, data: Partial<RiskItem>): Promise<ApiResponse<RiskItem>> {
    return apiService.put(`/whs/risk-items/${id}`, data);
  }
  deleteRiskItem(id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiService.delete(`/whs/risk-items/${id}`);
  }

  // ─── Checklist templates ────────────────────────────────────────────────────
  listChecklistTemplates(kind?: ChecklistKind): Promise<ApiResponse<ChecklistTemplate[]>> {
    return apiService.get(`/whs/checklist-templates${qs({ kind })}`);
  }
  createChecklistTemplate(data: Partial<ChecklistTemplate>): Promise<ApiResponse<ChecklistTemplate>> {
    return apiService.post('/whs/checklist-templates', data);
  }
  updateChecklistTemplate(id: number, data: Partial<ChecklistTemplate>): Promise<ApiResponse<ChecklistTemplate>> {
    return apiService.put(`/whs/checklist-templates/${id}`, data);
  }
  deleteChecklistTemplate(id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiService.delete(`/whs/checklist-templates/${id}`);
  }

  // ─── Inspections ────────────────────────────────────────────────────────────
  listInspections(filters: { status?: InspectionStatus } = {}): Promise<ApiResponse<InspectionListItem[]>> {
    return apiService.get(`/whs/inspections${qs(filters)}`);
  }
  getInspection(id: number): Promise<ApiResponse<Inspection>> {
    return apiService.get(`/whs/inspections/${id}`);
  }
  createInspection(data: Record<string, unknown>): Promise<ApiResponse<Inspection>> {
    return apiService.post('/whs/inspections', data);
  }
  updateInspection(id: number, data: Record<string, unknown>): Promise<ApiResponse<Inspection>> {
    return apiService.put(`/whs/inspections/${id}`, data);
  }
  deleteInspection(id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiService.delete(`/whs/inspections/${id}`);
  }

  // ─── Recurring inspection schedules ─────────────────────────────────────────
  listInspectionSchedules(activeOnly = false): Promise<ApiResponse<InspectionSchedule[]>> {
    return apiService.get(`/whs/inspection-schedules${qs({ active: activeOnly ? 'true' : undefined })}`);
  }
  createInspectionSchedule(data: Record<string, unknown>): Promise<ApiResponse<InspectionSchedule>> {
    return apiService.post('/whs/inspection-schedules', data);
  }
  updateInspectionSchedule(id: number, data: Record<string, unknown>): Promise<ApiResponse<InspectionSchedule>> {
    return apiService.put(`/whs/inspection-schedules/${id}`, data);
  }
  deleteInspectionSchedule(id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiService.delete(`/whs/inspection-schedules/${id}`);
  }

  // ─── Audits + findings ──────────────────────────────────────────────────────
  listAudits(filters: { status?: AuditStatus } = {}): Promise<ApiResponse<AuditListItem[]>> {
    return apiService.get(`/whs/audits${qs(filters)}`);
  }
  getAudit(id: number): Promise<ApiResponse<Audit>> {
    return apiService.get(`/whs/audits/${id}`);
  }
  createAudit(data: Partial<Audit>): Promise<ApiResponse<Audit>> {
    return apiService.post('/whs/audits', data);
  }
  updateAudit(id: number, data: Partial<Audit>): Promise<ApiResponse<Audit>> {
    return apiService.put(`/whs/audits/${id}`, data);
  }
  deleteAudit(id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiService.delete(`/whs/audits/${id}`);
  }
  createAuditFinding(auditId: number, data: Partial<AuditFinding>): Promise<ApiResponse<AuditFinding>> {
    return apiService.post(`/whs/audits/${auditId}/findings`, data);
  }
  updateAuditFinding(id: number, data: Partial<AuditFinding>): Promise<ApiResponse<AuditFinding>> {
    return apiService.put(`/whs/audit-findings/${id}`, data);
  }
  deleteAuditFinding(id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiService.delete(`/whs/audit-findings/${id}`);
  }

  // ─── Documents ──────────────────────────────────────────────────────────────
  listDocuments(filters: { doc_type?: DocumentType; status?: DocumentStatus; section?: 'whs' | 'employment' } = {}): Promise<ApiResponse<WhsDocument[]>> {
    return apiService.get(`/whs/documents${qs(filters)}`);
  }
  getDocument(id: number): Promise<ApiResponse<WhsDocument>> {
    return apiService.get(`/whs/documents/${id}`);
  }
  createDocument(data: Partial<WhsDocument>): Promise<ApiResponse<WhsDocument>> {
    return apiService.post('/whs/documents', data);
  }
  updateDocument(id: number, data: Partial<WhsDocument>): Promise<ApiResponse<WhsDocument>> {
    return apiService.put(`/whs/documents/${id}`, data);
  }
  deleteDocument(id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiService.delete(`/whs/documents/${id}`);
  }

  // ─── Training ───────────────────────────────────────────────────────────────
  listTraining(filters: { user_id?: number; expiring_within_days?: number } = {}): Promise<ApiResponse<TrainingRecord[]>> {
    return apiService.get(`/whs/training${qs(filters)}`);
  }
  createTraining(data: Partial<TrainingRecord>): Promise<ApiResponse<TrainingRecord>> {
    return apiService.post('/whs/training', data);
  }
  updateTraining(id: number, data: Partial<TrainingRecord>): Promise<ApiResponse<TrainingRecord>> {
    return apiService.put(`/whs/training/${id}`, data);
  }
  deleteTraining(id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiService.delete(`/whs/training/${id}`);
  }

  // ─── Emergency plans ────────────────────────────────────────────────────────
  listEmergencyPlans(): Promise<ApiResponse<EmergencyPlan[]>> {
    return apiService.get('/whs/emergency-plans');
  }
  getEmergencyPlan(id: number): Promise<ApiResponse<EmergencyPlan>> {
    return apiService.get(`/whs/emergency-plans/${id}`);
  }
  createEmergencyPlan(data: Partial<EmergencyPlan>): Promise<ApiResponse<EmergencyPlan>> {
    return apiService.post('/whs/emergency-plans', data);
  }
  updateEmergencyPlan(id: number, data: Partial<EmergencyPlan>): Promise<ApiResponse<EmergencyPlan>> {
    return apiService.put(`/whs/emergency-plans/${id}`, data);
  }
  deleteEmergencyPlan(id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiService.delete(`/whs/emergency-plans/${id}`);
  }

  // ─── Objectives ─────────────────────────────────────────────────────────────
  listObjectives(): Promise<ApiResponse<Objective[]>> {
    return apiService.get('/whs/objectives');
  }
  createObjective(data: Partial<Objective>): Promise<ApiResponse<Objective>> {
    return apiService.post('/whs/objectives', data);
  }
  updateObjective(id: number, data: Partial<Objective>): Promise<ApiResponse<Objective>> {
    return apiService.put(`/whs/objectives/${id}`, data);
  }
  deleteObjective(id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiService.delete(`/whs/objectives/${id}`);
  }

  // ─── Management reviews ─────────────────────────────────────────────────────
  listManagementReviews(): Promise<ApiResponse<ManagementReview[]>> {
    return apiService.get('/whs/management-reviews');
  }
  getManagementReview(id: number): Promise<ApiResponse<ManagementReview>> {
    return apiService.get(`/whs/management-reviews/${id}`);
  }
  createManagementReview(data: Partial<ManagementReview>): Promise<ApiResponse<ManagementReview>> {
    return apiService.post('/whs/management-reviews', data);
  }
  updateManagementReview(id: number, data: Partial<ManagementReview>): Promise<ApiResponse<ManagementReview>> {
    return apiService.put(`/whs/management-reviews/${id}`, data);
  }
  deleteManagementReview(id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiService.delete(`/whs/management-reviews/${id}`);
  }
}

export const whsService = new WhsService();
export default whsService;
